import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import Layout from "../../layouts";
import { toast } from "react-toastify";
import DynamicTable from "../../components/DynamicTable";
import apiService from "../../services/apiService";
import { withSearchIcon } from "../../utils/helper";

export default function MatchedRequestedUsers() {
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"matched" | "requested">(
    "matched"
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [exportingCSV, setExportingCSV] = useState(false);

  const location = useLocation();
  const queryEventId = new URLSearchParams(location.search).get("eventId");

  const [eventId, setEventId] = useState<any>(queryEventId);

  // ✅ Columns for matched/requested users
  const columns =
    activeTab === "matched"
      ? [
          { key: "sender_user_id", label: "Sender User ID" },
          { key: "sender_name", label: withSearchIcon("Sender Name") },
          { key: "receiver_user_id", label: "Receiver User ID" },
          { key: "receiver_name", label: withSearchIcon("Receiver Name") },
          { key: "sender_response_status", label: "Sender Response" },
          { key: "receiver_response_status", label: "Receiver Response" },
          {
            key: "event_id",
            label: "Event Id",
            render: (val: any) => {
              return val || "";
            },
          },
          {
            key: "event_name",
            label: withSearchIcon("Event Name"),
            render: (val: any) => {
              return val || "";
            },
          },
          { key: "location_name", label: "Location" },

          {
            key: "status",
            label: "Status",
            render: (val: string) => (
              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  val === "Matched"
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {val}
              </span>
            ),
          },
          {
            key: "date",
            label: "Date",
            render: (val: string) =>
              val
                ? new Date(val).toLocaleString("en-US", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                : "—",
          },
        ]
      : [
          { key: "sender_user_id", label: "Sender User ID" },
          { key: "sender_name", label: withSearchIcon("Sender Name") },
          { key: "receiver_user_id", label: "Receiver User ID" },
          { key: "receiver_name", label: withSearchIcon("Receiver Name") },
          { key: "sender_response_status", label: "Sender Response" },
          { key: "receiver_response_status", label: "Receiver Response" },
          {
            key: "event_id",
            label: "Event Id",
            render: (val: any) => {
              return val || "";
            },
          },
          {
            key: "event_name",
            label: withSearchIcon("Event Name"),
            render: (val: any) => {
              return val || "";
            },
          },
          { key: "location_name", label: "Location" },

          {
            key: "status",
            label: "Status",
            render: (val: string) => (
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                {val}
              </span>
            ),
          },
          {
            key: "date",
            label: "Date(Local)",
            render: (val: string) =>
              val
                ? new Date(val).toLocaleString("en-US", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                : "—",
          },
        ];

  // ✅ Tabs
  const tabs = [
    { label: "Matched Users", key: "matched" },
    { label: "Requested Users", key: "requested" },
  ];

  // ✅ Fetch when tab/page/search changes
  useEffect(() => {
    fetchUsers(page, activeTab, search);
  }, [page, activeTab, search, eventId]);

  // ✅ Fetch data
  const fetchUsers = async (
    pageNum: number,
    tabKey: string,
    search?: string
  ) => {
    try {
      setLoading(true);

      const endpoint =
        tabKey === "matched"
          ? "/users/get-matched-users"
          : "/users/get-requested-users";

      const params: any = {
        page: pageNum,
        limit: 50,
        search,
      };

      // ✅ Include eventId only if available
      if (eventId) params.eventId = eventId;

      const response: any = await apiService.get(endpoint, params);

      const data = response?.data?.data || response?.data?.users || [];

      setUsers(data);
      setTotalRecords(response?.data?.totalCount || data.length || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key: "matched" | "requested") => {
    setActiveTab(key);
    setSearch("");
    setPage(1);
  };

  const handleExportCSV = async () => {
    try {
      setExportingCSV(true);

      const endpoint =
        activeTab === "matched"
          ? "/export/export-matched-users"
          : "/export/export-requested-users";

      const params: any = {};
      if (eventId) params.eventId = eventId;

      const res: any = await apiService.get(endpoint, params, {
        responseType: "blob",
      });

      if (res) {
        const blob =
          res instanceof Blob
            ? res
            : new Blob([res], { type: "text/csv;charset=utf-8;" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          activeTab === "matched"
            ? "matched_users_export.csv"
            : "requested_users_export.csv"
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(
          activeTab === "matched"
            ? "Matched users CSV exported successfully."
            : "Requested users CSV exported successfully."
        );
      }
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error("Failed to export CSV. Please try again.");
    } finally {
      setExportingCSV(false);
    }
  };

  const handleSearch = (searchKey?: string | undefined) => {
    console.log("Searching for Event ID:", searchKey);

    // ✅ Update local state if needed
    setEventId(searchKey || "");
    setPage(1);
  };

  return (
    <Layout
      pageTitle="Matched & Requested Users"
      eventId={eventId}
      searchByEventId={{
        label: "Search by Event ID",
        onClick: (val?: any) => {
          handleSearch(val);
        }, // 👈 your custom handler
        className: "bg-green-600 hover:bg-green-700",
      }}
    >
      <div className="min-h-screen bg-gray-50 py-3 px-4 min-w-0">
        {/* ===== Tabs ===== */}
        <div className="flex bg-white border-b overflow-x-auto">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key as any)}
                className={`py-3 px-5 text-sm font-medium rounded-t-lg transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-blue-600 hover:bg-gray-50 border-b-2 border-transparent"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ===== Table ===== */}
        <div className="py-2 px-2">
          <DynamicTable
            title={
              activeTab === "matched" ? "Matched Users" : "Requested Users"
            }
            data={users}
            columns={columns}
            pageSize={50}
            loading={loading}
            activeNUmber={page}
            onPageChange={(newPage) => setPage(newPage)}
            totalRecords={totalRecords}
            totalPages={totalPages}
            onSearch={(value?: string) => {
              setActiveTab(activeTab);
              setPage(1);
              setSearch(value || "");
            }}
            hasTabs={true}
            exportTypes={["csv"]}
            handleExportEventsCSV={handleExportCSV}
            exportLoading={exportingCSV}
            exportCsvTitle={
              activeTab === "matched"
                ? "Export Matched Users CSV"
                : "Export Requested Users CSV"
            }
          />
        </div>
      </div>
    </Layout>
  );
}
